import {Component, EventEmitter, Inject, Input, OnInit, Output} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {merge} from 'lodash-es';
import {ApiResponse} from "../../../classes/api-response";
import {SnackService} from "../../../services/snack.service";
import {Box} from "../../../model/box";
import {BoxesService} from "../../../services/boxes-service";
import {ContractsService} from "../../../services/contracts.service";
import {SimsService} from "../../../services/sims.service";
import {Sim} from "../../../model/sim";
import {BoxInterface} from "../../../model/box-interface";
import {BoxInterfacesService} from "../../../services/box-interfaces.service";
import {Constants} from "../../../utils/constants";
import {MAT_DIALOG_DATA, MatDialog} from "@angular/material/dialog";
import {Interface} from "readline";
import {CreateSimDialogComponent} from "../../sims/create-sim-dialog/create-sim-dialog.component";
import {
  CreateBoxInterfaceDialogComponent
} from "../../box-interfaces/create-box-interface-dialog/create-box-interface-dialog.component";
import {PatchBox} from "../../../model/patch-box";
import {LocationsService} from 'src/app/services/locations.service';
import {BoxLocation} from 'src/app/model/box-location';
import {CreateBoxDTO} from 'src/app/model/dto/box/create-box-dto';
import {transformAndValidate, TransformValidationOptions} from 'class-transformer-validator';

@Component({
  selector: 'aw-box-form',
  templateUrl: './box-form.component.html',
  styleUrls: ['./box-form.component.scss']
})
export class BoxFormComponent implements OnInit {

  boxForm: UntypedFormGroup;
  @Output() saved = new EventEmitter<void>();
  @Output() canceled = new EventEmitter<void>();
  @Output() boxEmitter: EventEmitter<Box> = new EventEmitter<Box>();
  @Input() creatingFromSim = false;
  saving = false;
  loadingContracts = false;
  loadingSims = false;
  loadingBoxInterfaces = false;
  // loadingCertificates = false;
  loadingLocation = false;

  generalError?: ApiResponse;
  entities = Constants.entities;
  boxStatus = Constants.boxStatuses;
  simStatus = Constants.simStatuses;



  sims: Sim[] = [];
  // certificate: Certificate[] = [];
  boxInterfaces: BoxInterface[] = [];
  // contracts: Contract[] = [];
  boxLocations: BoxLocation[] = [];


  _box = new Box();
  // selectedContract?: Contract;
  isEditing = false;
  // selectedCertificate: Certificate | undefined = new Certificate();
  selectedBoxInterfaces?: Interface[] = [];
  selectedSim?: Sim;
  selectedLocation: BoxLocation = new BoxLocation();


  constructor(
    private boxService: BoxesService,
    private snackService: SnackService,
    private simsService: SimsService,
    private boxInterfacesService: BoxInterfacesService,
    private contractsService: ContractsService,
    private locationsService: LocationsService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: Box,
    // private certificatesService: CertificatesService
  ) {

    if (this.data.boxLocation)
      this.selectedLocation = this.data.boxLocation;



    this.boxForm = new UntypedFormGroup(
      {
        hardwareVersion: new UntypedFormControl(
          '',
          [
            Validators.required
          ]
        ),
        identifier: new UntypedFormControl(
          '',
          [
            Validators.required
          ]
        ),
        softwareVersion: new UntypedFormControl(
          '',
          [
            Validators.required
          ]
        ),
        status: new UntypedFormControl(
          this.boxStatus.onTheShelf,
          [
          ]
        ),
        sim: new UntypedFormControl(
          null,
          [
          ]
        ),
        boxInterfaces: new UntypedFormControl(
          [],
          [
          ]
        ),
        boxLocation: new UntypedFormControl(
          null,
          [
            Validators.required
          ]
        )
      }
    );
  }


  @Input() set box(value: Box) {
    this._box = merge(new Box(), value);
    // this.selectedContract = this._box.contracts?.find(contract => contract.status !== this.contractStatus.finalized);
    // this.selectedCertificate = this._contract.certificate;
    this.isEditing = !!this._box.id;
    const formValue = {
      hardwareVersion: this._box.hardwareVersion || '',
      identifier: this._box.identifier || '',
      softwareVersion: this._box.softwareVersion || '',
      status: this._box.status || this.boxStatus.onTheShelf,
      sim: this._box.sim || null,
      boxInterfaces: this._box.boxInterfaces || [],
      boxLocation: this._box.boxLocation || null  // Ensure location is properly set
    };
  
    this.boxForm.patchValue(formValue);
  
    // Set selectedLocation for display
    if (this._box.boxLocation) {
      this.selectedLocation = this._box.boxLocation;
    }
  }

  ngOnInit(): void {
    this.loadingLocation = true;
    if (!this.isEditing) {
      this.loadingContracts = true;
      this.loadingSims = true;
      this.loadingBoxInterfaces = true;
      // this.loadingCertificates = true;


      // this.contractsService.getAllContracts().subscribe(contracts => {
      //   this.contracts = contracts;
      //   this.loadingContracts = false;
      // });
      this.simsService.getAllSims([this.simStatus.unassigned]).subscribe(sims => {
        this.sims = sims;
        this.loadingSims = false;
      });
      this.boxInterfacesService.getAllBoxInterfaces().subscribe(boxInterfaces => {
        this.boxInterfaces = boxInterfaces;
        this.loadingBoxInterfaces = false;
      });
      // this.certificatesService.getAllCertificates().subscribe(certificates => {
      //   this.certificates = certificates;
      //   this.loadingCertificates = false;
      // });
    }
    this.locationsService.getAllLocations().subscribe(location => {
      this.boxLocations = location;
      this.loadingLocation = false;
    });
  }

  compareFn(obj1: any, obj2: any) {
    return obj1 && obj2 ? obj1.id === obj2.id : obj1 === obj2;
  }

  async saveBox(): Promise<void> {
    this.saving = true;

    this.generalError = undefined;

    if (this._box.id) {

      const newBox = new PatchBox();
      newBox.identifier = this.boxForm.value.identifier;
      newBox.hardwareVersion = this.boxForm.value.hardwareVersion;
      newBox.softwareVersion = this.boxForm.value.softwareVersion;
      newBox.boxLocation = this.boxForm.value.boxLocation;
      this.boxService.updateBox(newBox, this._box.id)
        .subscribe(
          () => {
            this.saving = false;
            this.box = newBox;
            this.snackService.success('Box updated!');
            this.saved.emit();
          },
          error => {
            this.saving = false;
            this.snackService.error(error.message);
            this.generalError = error;
          });
    } else {

      const newBox = merge(new Box(), this._box, this.boxForm.value);

      newBox.enabled = false;
      if (newBox.sim) {
        newBox.sim = newBox.sim.id;
      }
      if (newBox.boxInterfaces) {
        newBox.boxInterfaces = newBox.boxInterfaces.map((boxInterface: { id: any; }) => boxInterface.id);
      }
      if (newBox.boxLocation) {
        newBox.boxLocation = newBox.boxLocation.id;
      }
      const options : TransformValidationOptions  = {validator: { whitelist: true } };
      const newBoxDTO = await transformAndValidate(CreateBoxDTO, newBox,options) as CreateBoxDTO;

      this.boxService.createBox(newBoxDTO)
        .subscribe(
          createdBox => {
            this.saving = false;
            this.boxEmitter.emit(createdBox);
            this.box = createdBox;
            this.snackService.success('Box created!');
            this.saved.emit();
          },
          error => {
            this.saving = false;
            this.snackService.error(error.message);
            this.generalError = error;
          });
    }

  }

  openSimForm(): void {
    this.dialog.open(CreateSimDialogComponent, { data: { creatingFromBox: true } })
      .afterClosed()
      .subscribe(
        sim => {
          this.selectedSim = sim;
          if (this.selectedSim) {
            this.sims.push(sim);
          }
        });
  }


  openBoxInterfaceForm(): void {
    this.dialog.open(CreateBoxInterfaceDialogComponent, { data: { creatingFromBox: true } })
      .afterClosed()
      .subscribe(
        boxInterface => {
          if (boxInterface) {
            this.boxInterfaces.push(boxInterface);
            this.selectedBoxInterfaces?.push(boxInterface);
          }
        });
  }


}
